import React, { useState, useEffect } from 'react';
import './DepositNotification.css';

interface DepositNotificationProps {
  amount: number;
  onClose: () => void;
}

interface NotificationState {
  id: number;
  amount: number;
}

// Notification Manager for deposits
class DepositNotificationManager {
  private static instance: DepositNotificationManager;
  private notifications: NotificationState[] = [];
  private subscribers: ((notifications: NotificationState[]) => void)[] = [];
  private nextId = 1;

  private constructor() {}

  static getInstance(): DepositNotificationManager {
    if (!DepositNotificationManager.instance) {
      DepositNotificationManager.instance = new DepositNotificationManager();
    }
    return DepositNotificationManager.instance;
  }

  subscribe(callback: (notifications: NotificationState[]) => void): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  private notify(): void {
    this.subscribers.forEach(callback => callback([...this.notifications]));
  }

  addNotification(amount: number): void {
    const notification: NotificationState = {
      id: this.nextId++,
      amount
    };

    this.notifications.push(notification);
    this.notify();

    // Auto remove after 6 seconds
    setTimeout(() => {
      this.removeNotification(notification.id);
    }, 6000);
  }

  removeNotification(id: number): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notify();
  }
}

const notificationManager = DepositNotificationManager.getInstance();

// Individual Notification Component
const DepositNotification: React.FC<DepositNotificationProps> = ({ amount, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10);

    // Auto close after 6 seconds
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onClose, 300);
    }, 6000);

    return () => clearTimeout(timer);
  }, [onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 300);
  };

  return (
    <div 
      className={`deposit-notification ${isVisible ? 'visible' : ''} ${isExiting ? 'exiting' : ''}`}
      onClick={handleClose}
    >
      <div className="deposit-notification-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7V12C2 16.97 5.69 21.43 10.5 22.5L12 23L13.5 22.5C18.31 21.43 22 16.97 22 12V7L12 2Z" fill="url(#depositGradient)" opacity="0.2"/>
          <path d="M12 2L2 7V12C2 16.97 5.69 21.43 10.5 22.5L12 23L13.5 22.5C18.31 21.43 22 16.97 22 12V7L12 2Z" stroke="#7d19ff" strokeWidth="2"/>
          <path d="M9 12L11 14L15 10" stroke="#7d19ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <defs>
            <linearGradient id="depositGradient" x1="2" y1="2" x2="22" y2="22">
              <stop offset="0%" stopColor="#7d19ff"/>
              <stop offset="100%" stopColor="#6600eb"/>
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div className="deposit-notification-content">
        <div className="deposit-notification-title">Deposit Confirmed</div>
        <div className="deposit-notification-amount">+{amount.toFixed(4)} SOL</div>
      </div>
      <button className="deposit-notification-close" onClick={handleClose}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// Container Component
export const DepositNotificationContainer: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationState[]>([]);

  useEffect(() => {
    const unsubscribe = notificationManager.subscribe(setNotifications);
    return unsubscribe;
  }, []);

  return (
    <div className="deposit-notification-container">
      {notifications.map(notification => (
        <DepositNotification
          key={notification.id}
          amount={notification.amount}
          onClose={() => notificationManager.removeNotification(notification.id)}
        />
      ))}
    </div>
  );
};

// Export function to show deposit notification
export const showDepositNotification = (amount: number) => {
  notificationManager.addNotification(amount);
};

export default DepositNotification;
